/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  LuiMaterialTable,
  LuiToolbar,
  LuiCard,
  LuiEmptyState,
  LuiMaterialTableHeadCellStyles
} from '@jda/lui-common-component-library-mui5';
import { SignPlus } from '@jda/lui-common-icon-library-mui5';
import { Box, Button, TableCellProps, Tooltip, Typography } from '@mui/material';
import CustomTablePagination from '../../shared/CustomTablePagination';
import { AlertDialog } from '../../shared/Dialog/AlertDialog';
import DetailsModal from '../../shared/Dialog/DetailsModal';
import {
  getTopicSubscriptions,
  removeMachineTopicSubscriptions,
  deleteMachineTopicSubscription
} from '../../services/client';
import { displaySnackbar } from '../../store/snackBar/snackBarActionCreator';
import interpolateString from '../../utility/stringInterpolation';
import { MRT_ToggleFullScreenButton, MRT_ToggleDensePaddingButton } from 'material-react-table';
import useWindowDimensions from '../../utility/useWindowDimensions';
import {
  UNSUBSCRIBE,
  TOPICS_EMPTY_HEADER,
  TOPICS_EMPTY_TEXT,
  MAX_SUBSCRIPTION_SELECTION,
  ROWS_PER_PAGE_LABEL,
  ERROR_WHILE_FETCHING_SUBSCRIPTIONS,
  UNSUBSCRIBE_TOPICS,
  UNSUBSCRIBE_CONFIRMATION,
  CANCEL,
  TOPICS_UNSUBSCRIBED_SUCCESSFULLY,
  UNSUCCESSFUL_TOPIC_UNSUBSCRIBE_MESSAGE,
  UNSUCCESSFUL_UNSUBSCRIBE_TITLE,
  UNSUCCESSFUL_UNSUBSCRIBE_MESSAGE_TOPIC,
  ADD_TOPIC,
  LABELS
} from './constants';
import { useStyles } from './styles';

const INITIAL_PAGE_NUMBER = 1;
const INITIAL_PAGE_SIZE = 50;

const columns = [
  {
    header: LABELS.TOPIC_NAME,
    accessorKey: 'topicName',
    id: 'topicName',
    muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '60%'
        })
      } as TableCellProps)
  },
  {
    header: LABELS.TOPIC_ACCESS,
    accessorKey: 'topicAccess',
    id: 'topicAccess',
    muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '40%'
        })
      } as TableCellProps)
  }
];

interface TopicSubscriptionProps {
  machineId: string;
  machine: any;
}

const TopicSubscription = ({ machine, machineId }: TopicSubscriptionProps) => {

  const { height } = useWindowDimensions();
  const [pageNumber, setPageNumber] = useState(INITIAL_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [response, setResponse] = useState<any>();
  const [error, setError] = useState<any>();
  const [modules, setModules] = useState<any[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<number | ''>('');
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<any[]>([]);
  const [unsubscribingTopics, setUnsubscribingTopics] = useState<any[]>([]);
  const [isRequesting, setIsRequesting] = useState(false);
  const [erroredSubscriptions, setErroredSubscriptions] = useState<any[]>([]);

  const moduleData = useMemo(() => modules.map(module => ({ ...module, key: module.id, displayName: module.name })), [
    modules
  ]);

  const selectedModule = useMemo(() => moduleData.find(module => module.id === selectedModuleId), [
    moduleData,
    selectedModuleId
  ]);
  const mappedData = useMemo(
    () =>
      response?.data?.length > 0
        ? response.data[0]?.subscriptionTopics?.map((topic: any, index: number) => ({ ...topic, id: `${index + 1}` }))
        : [],
    [response]
  );

  const history = useHistory();

  const dispatch = useDispatch();

  const { classes } = useStyles();

  const fetchMachineSubscriptions = useCallback(
    async (module, itemsPerPage: number, page: number) => {
      setSelectedModuleId(module ? module.id : '');
      setPageSize(itemsPerPage);
      setPageNumber(page);
      try {
        const response = await getTopicSubscriptions(module, machine.subjectId, {
          pageNumber: page,
          pageSize: itemsPerPage
        });
        setResponse(response);
      } catch (error: any) {
        const errMessage = error.response?.data?.message || error.message;
        dispatch(displaySnackbar(interpolateString([errMessage], ERROR_WHILE_FETCHING_SUBSCRIPTIONS), 'error'));
        setResponse({ data: [], metadata: { totalCount: 0 } });
        setError(error.response.data || error);
      }
    },
    [machine.subjectId, dispatch]
  );

  useEffect(() => {
    fetchMachineSubscriptions('', INITIAL_PAGE_SIZE, INITIAL_PAGE_NUMBER);
  }, [fetchMachineSubscriptions]);

  const handleAddSubscriptionClick = async () => {
    if (history.location.search) {
      history.push(`/machineDetail/topicSubscriptions/${machineId}${history.location.search}`);
    } else {
      history.push(`/machineDetail/topicSubscriptions/${machineId}`);
    }
  };

  const handleUnsubscribeSelected = () => {
    setUnsubscribingTopics(selectedSubscriptions);
  };

  const handleUnsubscribeSave = async () => {
    setIsRequesting(true);
    try {
      if (response?.data[0]?.subscriptionTopics.length === unsubscribingTopics.length) {
        await deleteMachineTopicSubscription(response?.data[0]?.name)
          .then()
          .catch(error => {
            throw error;
          });
      } else {
        await removeMachineTopicSubscriptions(machine.subjectId, response?.data[0]?.name, unsubscribingTopics)
          .then()
          .catch(error => {
            throw error;
          });
      }
      setIsRequesting(false);
      setSelectedSubscriptions([]);
      setUnsubscribingTopics([]);
      fetchMachineSubscriptions('', pageSize, 1);
      dispatch(displaySnackbar(TOPICS_UNSUBSCRIBED_SUCCESSFULLY, 'success'));
    } catch (error: any) {
      setIsRequesting(false);
      dispatch(displaySnackbar(interpolateString([error.message], UNSUCCESSFUL_TOPIC_UNSUBSCRIBE_MESSAGE), 'error'));
    }
  };

  const handleUnsubscribeCancel = () => {
    setUnsubscribingTopics([]);
  };

  const handleErroredApisModalClose = () => {
    setErroredSubscriptions([]);
  };
  const handleChangePageOptions = (itemsPerPage: number, page: number) => {
    fetchMachineSubscriptions(selectedModule, itemsPerPage, page - 1);
  };
  const extendedToolbar = ({ table }) => {
    // const leftComponents = {
    //   component: (
    //     <div>
    //       <LuiSelect
    //         itemData={moduleData}
    //         selectedItem={selectedModule}
    //         placeholder="Select Module"
    //         inputLabel=""
    //         width={DROPDOWN_WIDTH}
    //         maxHeight={DROPDOWN_MAX_HEIGHT}
    //         onSelectionChange={handleModuleSelectionChange}
    //         size="small"
    //       />
    //     </div>
    //   )
    // };
    const rightComponents = {
      component: (
        <div className={classes.toolbarButtonPanel}>
          <Tooltip arrow title={ADD_TOPIC} placement="bottom">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleAddSubscriptionClick}
              startIcon={<SignPlus />}
              size="small"
            >
              {ADD_TOPIC}
            </Button>
          </Tooltip>
          <Tooltip arrow title={UNSUBSCRIBE_TOPICS} placement="bottom">
            <Button
              variant="outlined"
              color="primary"
              disabled={selectedSubscriptions.length === 0 || selectedSubscriptions.length > MAX_SUBSCRIPTION_SELECTION}
              onClick={handleUnsubscribeSelected}
              size="small"
            >
              {UNSUBSCRIBE_TOPICS}
            </Button>
          </Tooltip>
          <MRT_ToggleDensePaddingButton table={table} />
          <MRT_ToggleFullScreenButton table={table} />
        </div>
      )
    };
    return <LuiToolbar className={classes.toolbarPanel} rightZone={rightComponents} />;
  };
  const emptyTableFallback = () => {
    return (
      <Box my={5}>
        <LuiEmptyState imageItem={null} heading={TOPICS_EMPTY_HEADER} subHeading={TOPICS_EMPTY_TEXT} />
      </Box>
    );
  };

  return (
    <>
      <LuiCard
        elevation={0}
        hasBorder={false}
        // headerBordered={true}
        hasCardContentPadding={false}
        // headerComponent={<div className={classes.cardHeader}>
        //   <Typography variant="h2" color="textPrimary">{SUBSCRIBED_TOPICS}</Typography>
        //   <Typography variant="body1" color="textPrimary">{
        //   selectedSubscriptions.length > 0
        //     ? `(${selectedSubscriptions.length} ${SELECTED_OF} ${response?.metadata?.totalCount})`
        //     : ''
        // }</Typography>
        // </div>}
      >
        <CustomTablePagination
          data={mappedData}
          loading={!response}
          withoutPagination={false}
          totalCount={response?.metadata?.totalCount}
          columns={columns}
          selectedRowsData={selectedSubscriptions}
          onSelectedRowsDataChange={setSelectedSubscriptions}
          rowsPerPageOptions={[20, 50, 100]}
          labelRowsPerPage={ROWS_PER_PAGE_LABEL}
          currentPageNumber={pageNumber}
          currentItemsPerPage={pageSize}
          onChangePageOptions={handleChangePageOptions}
          renderTopToolbar={extendedToolbar}
          renderEmptyState={emptyTableFallback}
          maxTableBodyHeight={height - 240}
        />
      </LuiCard>
      {unsubscribingTopics.length > 0 && (
        <AlertDialog
          dialogContentText={UNSUBSCRIBE_TOPICS}
          dialogTitle={`${UNSUBSCRIBE_CONFIRMATION} `}
          recordName={
            unsubscribingTopics.length === 1
              ? unsubscribingTopics[0].topicName
              : `${unsubscribingTopics.length} ${LABELS.TOPICS}`
          }
          hideCanNotBeUndoneMessage={true}
          primaryButtonText={UNSUBSCRIBE}
          secondButtonText={CANCEL}
          handleOnOk={handleUnsubscribeSave}
          handleOnCancel={handleUnsubscribeCancel}
          showDialogLoader={isRequesting}
        />
      )}
      {erroredSubscriptions.length > 0 && (
        <DetailsModal
          openModal={true}
          handleClose={handleErroredApisModalClose}
          dialogTitle={UNSUCCESSFUL_UNSUBSCRIBE_TITLE}
        >
          <Typography>{UNSUCCESSFUL_UNSUBSCRIBE_MESSAGE_TOPIC}</Typography>
          <LuiMaterialTable data={erroredSubscriptions} keyAccessor="id" columns={columns} enableAlternateRowStyle />
        </DetailsModal>
      )}
    </>
  );
};

export default TopicSubscription;
